import { CompaniesI } from '../interfaces/companies.interface'
import { CompaniesService } from '../services/companies.service'
import {
    BusinessModelsGetListResponseT,
    CompaniesCreateCompanyResponseT,
    CompaniesDownloadExcelPayloadT,
    CompaniesDownloadExcelResponseT,
    CompaniesGenerateContractResponseT,
    CompaniesGetActivityResponse,
    CompaniesGetAgreementBasesResponseT,
    CompaniesGetAllListParamsT,
    CompaniesGetCompanyResponseT,
    CompaniesGetCompanySettingsParams,
    CompaniesGetCompanySettingsResponse,
    CompaniesGetCompanySourcesResponse,
    CompaniesGetCountriesAndManagersResponseT,
    CompaniesGetCountriesResponseT,
    CompaniesGetListBillingParams,
    CompaniesGetListParams,
    CompaniesGetListResponseT,
    CompaniesGetPeriodsResponseT,
    CompaniesGetSchedulesResponseT,
    CompaniesGetStagesResponseT,
    CompaniesGetSubdivisionsResponseT,
    CompaniesGetTariffsResponse,
    CompaniesGetTransportTypesResponseT,
    CompaniesGetWeightTypesResponseT,
    CompaniesUpdateCompanyResponseT,
    CompanyGenerateContractPayloadT,
    CompanyGetOrderTypesResponse,
    CompanyGetTranscriptTypesResponse,
    CompanyPayloadT,
    CompanySetBillingPayloadT,
    CompanySetClientPayloadT,
    GetCompaniesParams,
    GetCompanyContractsResponse,
    GetPayersResponseT,
} from '../types/companies.types'

const companiesService = new CompaniesService()

class CompaniesRealisation implements CompaniesI {
    getAllList(params: CompaniesGetAllListParamsT): Promise<CompaniesGetListResponseT> {
        return companiesService.getAllList(params)
    }

    getCompanies(params: GetCompaniesParams): Promise<CompaniesGetListResponseT> {
        return companiesService.getCompanies(params)
    }

    getTransportTypes(): Promise<CompaniesGetTransportTypesResponseT> {
        return companiesService.getTransportTypes()
    }

    getCompany(company_id: number): Promise<CompaniesGetCompanyResponseT> {
        return companiesService.getCompany(company_id)
    }

    getCompanySettings(
        company_id: number,
        params: CompaniesGetCompanySettingsParams
    ): Promise<CompaniesGetCompanySettingsResponse> {
        return companiesService.getCompanySettings(company_id, params)
    }

    getList(params: CompaniesGetListParams): Promise<CompaniesGetListResponseT> {
        return companiesService.getList(params)
    }

    getListBilling(params: CompaniesGetListBillingParams): Promise<CompaniesGetListResponseT> {
        return companiesService.getListBilling(params)
    }

    getSubdivisions(params: any): Promise<CompaniesGetSubdivisionsResponseT> {
        return companiesService.getSubdivisions(params)
    }

    getCountriesAndManagers(): Promise<CompaniesGetCountriesAndManagersResponseT> {
        return companiesService.getCountriesAndManagers()
    }

    getCountries(page: number, search?: string): Promise<CompaniesGetCountriesResponseT> {
        return companiesService.getCountries(page, search)
    }

    createCompany(company_payload: CompanyPayloadT): Promise<CompaniesCreateCompanyResponseT> {
        return companiesService.createCompany(company_payload)
    }

    setClient(payload: CompanySetClientPayloadT): Promise<void> {
        return companiesService.setClient(payload)
    }

    getPayers(page: number, company_id: number): Promise<GetPayersResponseT> {
        return companiesService.getPayers(page, company_id)
    }

    sendPayers(company_id: number, payer_company_data: any[]): Promise<void> {
        return companiesService.sendPayers(company_id, payer_company_data)
    }

    resetPassword(email: string, password: string): Promise<void> {
        return companiesService.resetPassword(email, password)
    }

    changeManager(oldManager: number, newManager: number): Promise<void> {
        return companiesService.changeManager(oldManager, newManager)
    }

    changeManagerByExcel(form_data: FormData): Promise<void> {
        return companiesService.changeManagerByExcel(form_data)
    }

    updateCompany(
        company_id: number,
        company_payload: CompanyPayloadT
    ): Promise<CompaniesUpdateCompanyResponseT> {
        return companiesService.updateCompany(company_id, company_payload)
    }

    enableVerification(company_id: number, type_id: number): Promise<void> {
        return companiesService.enableVerification(company_id, type_id)
    }

    setBilling(company_id: number, params: CompanySetBillingPayloadT): Promise<void> {
        return companiesService.setBilling(company_id, params)
    }

    disableVerification(company_id: number): Promise<void> {
        return companiesService.disableVerification(company_id)
    }

    activateCompany(company_id: number): Promise<void> {
        return companiesService.activateCompany(company_id)
    }

    enableMassApprove(company_id: number): Promise<void> {
        return companiesService.enableMassApprove(company_id)
    }

    disableMassApprove(company_id: number): Promise<void> {
        return companiesService.disableMassApprove(company_id)
    }

    confirmCarrier(id: number): Promise<void> {
        return companiesService.confirmCarrier(id)
    }

    deactivateCompany(company_id: number): Promise<void> {
        return companiesService.deactivateCompany(company_id)
    }

    generateContract(
        company_id: number,
        contract_payload: CompanyGenerateContractPayloadT
    ): Promise<CompaniesGenerateContractResponseT> {
        return companiesService.generateContract(company_id, contract_payload)
    }

    getCompanyBases(): Promise<CompaniesGetAgreementBasesResponseT> {
        return companiesService.getCompanyBases()
    }

    getBusinessModels(): Promise<BusinessModelsGetListResponseT> {
        return companiesService.getBusinessModels()
    }

    activateCompanySetting(company_id: number, setting_id: number): Promise<void> {
        return companiesService.activateCompanySetting(company_id, setting_id)
    }

    disableCompanySetting(company_id: number, setting_id: number): Promise<void> {
        return companiesService.disableCompanySetting(company_id, setting_id)
    }

    resendCompanyContract(company_id: number): Promise<void> {
        return companiesService.resendCompanyContract(company_id)
    }

    resendCompanyTo1C(company_id: number): Promise<void> {
        return companiesService.resendCompanyTo1C(company_id)
    }

    sendToContract(company_id: number): Promise<void> {
        return companiesService.sendToContract(company_id)
    }

    getSchedules(params: any): Promise<CompaniesGetSchedulesResponseT> {
        return companiesService.getSchedules(params)
    }

    getActivity(
        companyId: number,
        createdAtFrom?: string | null,
        createdAtTo?: string | null
    ): Promise<CompaniesGetActivityResponse> {
        return companiesService.getActivity(companyId, createdAtFrom, createdAtTo)
    }

    getStages(page: number): Promise<CompaniesGetStagesResponseT> {
        return companiesService.getStages(page)
    }

    getPeriods(page: number): Promise<CompaniesGetPeriodsResponseT> {
        return companiesService.getPeriods(page)
    }

    getWeightTypes(page: number): Promise<CompaniesGetWeightTypesResponseT> {
        return companiesService.getWeightTypes(page)
    }

    getTariffs(): Promise<CompaniesGetTariffsResponse> {
        return companiesService.getTariffs()
    }

    getOrderTypes(): Promise<CompanyGetOrderTypesResponse> {
        return companiesService.getOrderTypes()
    }

    getTranscriptTypes(): Promise<CompanyGetTranscriptTypesResponse> {
        return companiesService.getTranscriptTypes()
    }

    getCompanySources(): Promise<CompaniesGetCompanySourcesResponse> {
        return companiesService.getCompanySources()
    }

    exportExcel(params: CompaniesDownloadExcelPayloadT): Promise<CompaniesDownloadExcelResponseT> {
        return companiesService.exportExcel(params)
    }

    importCompanies(payload: FormData): Promise<void> {
        return companiesService.importCompanies(payload)
    }

    enableSetting(company_id: number, setting_id: number): Promise<void> {
        return companiesService.enableSetting(company_id, setting_id)
    }

    disableSetting(company_id: number, setting_id: number): Promise<void> {
        return companiesService.disableSetting(company_id, setting_id)
    }

    getCompanyContracts(companyId: number): Promise<GetCompanyContractsResponse> {
        return companiesService.getCompanyContracts(companyId)
    }
}

export const companies = new CompaniesRealisation()
